import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type ColumnMappings,
  type MultiTypeMappingSuggestion,
  type NewPropertySuggestion,
  type SelectOptionSuggestion,
  suggestSelectOptions,
  type UploadedFileEntry,
} from '@/lib/csv-import/api';
import { logger } from '@/lib/logger';

// Deterministic 6-hex-digit fallback for labels whose slug is empty (e.g. a
// label made entirely of non-ASCII characters). Same label always hashes to
// the same suffix, so re-deriving the name for an unchanged label is stable.
function hashLabelToHex(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash << 5) - hash + label.charCodeAt(i);
    hash |= 0;
  }
  return (hash >>> 0).toString(16).padStart(6, '0').slice(-6);
}

// Stable value-key for a select property's options, used to detect whether
// the user actually changed the option list (undefined and [] both -> '[]').
function optionsKey(options?: SelectOptionSuggestion[]): string {
  return JSON.stringify((options ?? []).map(o => [o.value, o.label]));
}

// Key for the per-entry options request counter: the same (file, property,
// entity type) composite every write is scoped to.
function optionsSeqKey(
  fileIdStr: string,
  propertyName: string,
  entityTypeName: string
): string {
  return `${fileIdStr}::${propertyName}::${entityTypeName}`;
}

export interface UsePropertyManagementReturn {
  userNewProperties: Record<string, NewPropertySuggestion[]>;
  setUserNewProperties: React.Dispatch<
    React.SetStateAction<Record<string, NewPropertySuggestion[]>>
  >;
  handleCreateProperty: (
    mappingKey: string | number,
    csvColumn: string,
    label: string
  ) => void;
  handleUserNewPropertyFieldTypeChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newFieldType: string
  ) => void;
  handleUserNewPropertyOptionsChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    options: SelectOptionSuggestion[]
  ) => void;
  handleUserNewPropertyLabelChange: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string,
    newLabel: string
  ) => void;
  handleGenerateSelectOptions: (
    fileId: string | number,
    propertyName: string,
    entityTypeName: string
  ) => void;
}

/**
 * Sub-hook managing user-initiated new properties for the CSV import wizard.
 *
 * Owns the `userNewProperties` state and the callbacks for creating and
 * updating those properties. When field type changes to 'select', fetches
 * suggested options from the API and stores them on the property.
 */
export function usePropertyManagement(
  uploadedFiles: UploadedFileEntry[],
  setColumnMappings: React.Dispatch<React.SetStateAction<ColumnMappings>>,
  orgId: number,
  multiTypeSuggestions: Record<string | number, MultiTypeMappingSuggestion>
): UsePropertyManagementReturn {
  const [userNewProperties, setUserNewProperties] = useState<
    Record<string, NewPropertySuggestion[]>
  >({});

  // Keep refs to avoid stale closures in callbacks.
  const uploadedFilesRef = useRef(uploadedFiles);
  useEffect(() => {
    uploadedFilesRef.current = uploadedFiles;
  }, [uploadedFiles]);

  // Multi-type files carry their AI-suggested new properties on the
  // per-entity-type sections of multiTypeSuggestions (keyed by real file id),
  // NOT on uploadedFiles[].new_properties. The New Properties panel renders
  // both sources, so the copy-from-AI fallbacks below must consult this ref to
  // materialize an edit to a multi-type suggestion.
  const multiTypeSuggestionsRef = useRef(multiTypeSuggestions);
  useEffect(() => {
    multiTypeSuggestionsRef.current = multiTypeSuggestions;
  }, [multiTypeSuggestions]);

  const userNewPropertiesRef = useRef(userNewProperties);
  useEffect(() => {
    userNewPropertiesRef.current = userNewProperties;
  }, [userNewProperties]);

  // Monotonic counter, keyed per (file, property, entity type), identifying the
  // latest authoritative intent for that entry's options. A resolved
  // suggestSelectOptions response is applied only if its captured generation
  // still matches — so a late response cannot clobber options the user (or a
  // newer transition) changed after it was issued. Keyed on the same composite
  // the writes are scoped to: the New Properties panel fans a single card edit
  // out to every file carrying that (property, entity type), and each file's
  // fetch must resolve independently rather than the last file's request
  // cancelling its siblings'.
  const optionsRequestSeqRef = useRef<Record<string, number>>({});

  // Create a new user-initiated property for a column.
  //
  // `mappingKey` is the key the calling row reads its column mappings from:
  // the plain file id for a standard file, or the `${fileId}_virtual_${type}`
  // editKey for a multi-type section. The column mapping MUST be written under
  // that exact key — writing under the plain file id when the row reads the
  // virtual key leaves the mapping invisible to the UI and to submit, orphaning
  // the new property (the column then silently fails to import).
  const handleCreateProperty = useCallback(
    (mappingKey: string | number, csvColumn: string, label: string) => {
      const trimmed = label.trim();
      if (!trimmed) {
        return;
      }
      const mappingKeyStr = String(mappingKey);
      // Recover the source file id and entity type from a virtual editKey.
      const virtualMatch = /^(.+)_virtual_(.+)$/.exec(mappingKeyStr);
      const fileIdStr = virtualMatch ? virtualMatch[1] : mappingKeyStr;
      const rawSlug = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
      // Backend requires a lowercase letter immediately after the `org_`
      // prefix (org_[a-z][a-z0-9_]*). rawSlug can start with a digit (e.g.
      // "2024 Revenue" -> "2024_revenue") or be empty (a label made entirely
      // of non-ASCII characters). The user typed a label, not this derived
      // name — it appears nowhere else in the wizard — so repair it here
      // rather than rejecting the label with a validation error.
      const needsRepair = rawSlug === '' || !/^[a-z]/.test(rawSlug);
      const slug = needsRepair
        ? `custom_${rawSlug || hashLabelToHex(trimmed)}`
        : rawSlug;
      const propertyName = `org_${slug}`;

      const file = uploadedFilesRef.current.find(
        f => String(f.file_id) === fileIdStr
      );
      if (!file) {
        return;
      }
      // A multi-type section's entity type comes from its editKey; it can
      // differ from the file's primary entity type. Submit filters user
      // properties per entity type, so stamping the file's type here would
      // drop the property from the payload.
      const entityTypeName = virtualMatch
        ? virtualMatch[2]
        : file.entity_type_name;

      // Repair can make two distinct labels derive the same property_name
      // (e.g. two labels that both start with digits). Reuse an existing
      // property only when the collision is genuinely the same label;
      // otherwise disambiguate so the second property isn't silently
      // dropped by the dedupe check below.
      const existingForFile = userNewPropertiesRef.current[fileIdStr] ?? [];
      const collision = existingForFile.find(
        np =>
          np.property_name === propertyName &&
          np.entity_type_name === entityTypeName
      );
      let finalPropertyName = propertyName;
      if (collision && collision.label !== trimmed) {
        let suffix = 2;
        let candidate = `${propertyName}_${suffix}`;
        while (
          existingForFile.some(
            np =>
              np.property_name === candidate &&
              np.entity_type_name === entityTypeName
          )
        ) {
          suffix += 1;
          candidate = `${propertyName}_${suffix}`;
        }
        finalPropertyName = candidate;
      }

      const newProp: NewPropertySuggestion = {
        column: csvColumn,
        property_name: finalPropertyName,
        label: trimmed,
        field_type_name: 'text',
        entity_type_name: entityTypeName,
      };

      setUserNewProperties(prev => {
        const existing = prev[fileIdStr] ?? [];
        if (
          existing.some(
            np =>
              np.property_name === finalPropertyName &&
              np.entity_type_name === entityTypeName
          )
        ) {
          return prev;
        }
        return { ...prev, [fileIdStr]: [...existing, newProp] };
      });

      setColumnMappings(prev => ({
        ...prev,
        [mappingKeyStr]: {
          ...(prev[mappingKeyStr] ?? {}),
          [csvColumn]: finalPropertyName,
        },
      }));
    },
    [setColumnMappings]
  );

  // Shared three-tier baseline lookup: user-created (userNewPropertiesRef) ->
  // standard AI suggestion (uploadedFilesRef) -> multi-type AI suggestion
  // (multiTypeSuggestionsRef, whose new properties live on the per-entity-type
  // sections keyed by file id, NOT on uploadedFiles[].new_properties — see
  // #11333/#11395). Keeping this in one place keeps all callers' fallback
  // precedence behaviorally symmetric as it evolves.
  const findExistingNewProperty = useCallback(
    (
      fileIdStr: string,
      matches: (np: NewPropertySuggestion) => boolean
    ): NewPropertySuggestion | undefined => {
      const userNp = userNewPropertiesRef.current[fileIdStr]?.find(matches);
      if (userNp) {
        return userNp;
      }
      const aiProp = uploadedFilesRef.current
        .find(f => String(f.file_id) === fileIdStr)
        ?.new_properties?.find(matches);
      if (aiProp) {
        return aiProp;
      }
      const mt = multiTypeSuggestionsRef.current[fileIdStr];
      if (mt) {
        for (const et of mt.entity_types) {
          const mtAiProp = et.new_properties.find(matches);
          if (mtAiProp) {
            return mtAiProp;
          }
        }
      }
      return undefined;
    },
    []
  );

  // Shared three-tier write helper: applies `update` to the first matched entry
  // in prev[fileIdStr] (user-created, updated in place), or copies the standard
  // AI or multi-type AI suggestion into prev[fileIdStr] with the update applied
  // (appending). Returns prev unchanged when no match is found anywhere.
  const applyNewPropertyEdit = useCallback(
    (
      prev: Record<string, NewPropertySuggestion[]>,
      fileIdStr: string,
      matches: (np: NewPropertySuggestion) => boolean,
      update: (np: NewPropertySuggestion) => NewPropertySuggestion
    ): Record<string, NewPropertySuggestion[]> => {
      const fileProps = prev[fileIdStr];
      if (fileProps) {
        const idx = fileProps.findIndex(matches);
        if (idx !== -1) {
          return {
            ...prev,
            [fileIdStr]: fileProps.map((np, i) =>
              i === idx ? update(np) : np
            ),
          };
        }
      }
      const aiProp = uploadedFilesRef.current
        .find(f => String(f.file_id) === fileIdStr)
        ?.new_properties?.find(matches);
      if (aiProp) {
        const existing = prev[fileIdStr] ?? [];
        return {
          ...prev,
          [fileIdStr]: [...existing, update(aiProp)],
        };
      }
      const mt = multiTypeSuggestionsRef.current[fileIdStr];
      if (mt) {
        for (const et of mt.entity_types) {
          const mtAiProp = et.new_properties.find(matches);
          if (mtAiProp) {
            const existing = prev[fileIdStr] ?? [];
            return {
              ...prev,
              [fileIdStr]: [...existing, update(mtAiProp)],
            };
          }
        }
      }
      return prev;
    },
    []
  );

  // Update the field type of a new property. When changing to 'select', fetches
  // suggested options by having the backend read the full CSV column data.
  // When changing away, clears options.
  const handleUserNewPropertyFieldTypeChange = useCallback(
    (
      fileId: string | number,
      propertyName: string,
      entityTypeName: string,
      newFieldType: string
    ) => {
      const fileIdStr = String(fileId);
      const matches = (np: NewPropertySuggestion): boolean =>
        np.property_name === propertyName &&
        np.entity_type_name === entityTypeName;

      const previousFieldType = findExistingNewProperty(
        fileIdStr,
        matches
      )?.field_type_name;

      // Collect column and ALL numeric file IDs where (property, entityType)
      // exists across all files — these are the FETCH inputs. Do NOT scope to
      // a single file; only the writes below are scoped.
      let column: string | undefined;
      const numericFileIds: number[] = [];
      for (const [fid, props] of Object.entries(userNewPropertiesRef.current)) {
        const found = props.find(matches);
        if (found) {
          if (!column) {
            ({ column } = found);
          }
          const numId = Number(fid);
          if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
            numericFileIds.push(numId);
          }
        }
      }
      for (const uf of uploadedFilesRef.current) {
        const aiProp = uf.new_properties?.find(matches);
        if (aiProp) {
          if (!column) {
            ({ column } = aiProp);
          }
          const numId =
            typeof uf.file_id === 'number' ? uf.file_id : Number(uf.file_id);
          if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
            numericFileIds.push(numId);
          }
        }
      }
      // Multi-type AI-only suggestions: their new properties live on the
      // per-entity-type sections keyed by file id. Include them so a
      // text->select change on a multi-type AI-only property can still resolve
      // the column and file id needed to fetch suggested options (see #11395).
      for (const [fid, mt] of Object.entries(multiTypeSuggestionsRef.current)) {
        for (const et of mt.entity_types) {
          const mtAiProp = et.new_properties.find(matches);
          if (mtAiProp) {
            if (!column) {
              ({ column } = mtAiProp);
            }
            const numId = Number(fid);
            if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
              numericFileIds.push(numId);
            }
            break;
          }
        }
      }

      // Synchronously update field type scoped to (fileId, matches); clear
      // options when leaving 'select'.
      setUserNewProperties(prev =>
        applyNewPropertyEdit(prev, fileIdStr, matches, np => ({
          ...np,
          field_type_name: newFieldType,
          options: newFieldType === 'select' ? np.options : undefined,
        }))
      );

      // Leaving 'select' invalidates any in-flight suggest/generate fetch for
      // this property: a late response must not re-append its select options
      // onto a property the user just changed to another type (which would
      // otherwise resurrect a duplicate select entry via the AI fallback).
      const seqKey = optionsSeqKey(fileIdStr, propertyName, entityTypeName);
      if (previousFieldType === 'select' && newFieldType !== 'select') {
        optionsRequestSeqRef.current[seqKey] =
          (optionsRequestSeqRef.current[seqKey] ?? 0) + 1;
      }

      // Async: fetch suggested options only on a genuine transition into 'select'.
      if (
        newFieldType === 'select' &&
        previousFieldType !== 'select' &&
        column &&
        numericFileIds.length > 0
      ) {
        const seq = (optionsRequestSeqRef.current[seqKey] ?? 0) + 1;
        optionsRequestSeqRef.current[seqKey] = seq;

        suggestSelectOptions(orgId, numericFileIds, column)
          .then(result => {
            setUserNewProperties(prev => {
              const fileProps = prev[fileIdStr];
              if (fileProps) {
                const idx = fileProps.findIndex(matches);
                if (
                  idx !== -1 &&
                  fileProps[idx].field_type_name === 'select' &&
                  optionsRequestSeqRef.current[seqKey] === seq
                ) {
                  return {
                    ...prev,
                    [fileIdStr]: fileProps.map((np, i) =>
                      i === idx ? { ...np, options: result.options } : np
                    ),
                  };
                }
              }
              return prev;
            });
          })
          .catch(err => {
            logger.error(
              'Failed to fetch suggested select options',
              err instanceof Error ? err : new Error(String(err)),
              { propertyName }
            );
          });
      }
    },
    [orgId, findExistingNewProperty, applyNewPropertyEdit]
  );

  // Regenerate a select property's options from the CSV ("Generate options from
  // CSV" action). This shares the per-property generation guard with the
  // transition fetch (handleUserNewPropertyFieldTypeChange) and user edits
  // (handleUserNewPropertyOptionsChange): it bumps the counter before fetching
  // and applies the response only if that generation is still current — so a
  // late regenerate response can never clobber options the user edited (or a
  // newer request produced) after the button was clicked. Keeping this fetch in
  // the hook, rather than in the panel, is what lets it share the one guard;
  // two independent guards ordered by value-equality could apply the older
  // result of two overlapping requests.
  const handleGenerateSelectOptions = useCallback(
    (fileId: string | number, propertyName: string, entityTypeName: string) => {
      const fileIdStr = String(fileId);
      const matches = (np: NewPropertySuggestion): boolean =>
        np.property_name === propertyName &&
        np.entity_type_name === entityTypeName;

      // Collect the column name and every numeric file id where this
      // (property, entity type) exists. Match on entity type so two entity
      // types sharing a property name in a multi-type file don't collide.
      // The FETCH inputs stay aggregated across files (the suggested options
      // are derived from the CSV data wherever the property appears); only the
      // WRITE below is scoped to the file whose card was clicked.
      let column: string | undefined;
      const numericFileIds: number[] = [];

      for (const [fid, props] of Object.entries(userNewPropertiesRef.current)) {
        const found = props.find(matches);
        if (found) {
          if (!column) {
            ({ column } = found);
          }
          const numId = Number(fid);
          if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
            numericFileIds.push(numId);
          }
        }
      }
      for (const uf of uploadedFilesRef.current) {
        const found = uf.new_properties?.find(matches);
        if (found) {
          if (!column) {
            ({ column } = found);
          }
          const numId =
            typeof uf.file_id === 'number' ? uf.file_id : Number(uf.file_id);
          if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
            numericFileIds.push(numId);
          }
        }
      }
      // Multi-type files carry their AI-suggested new properties on the
      // per-entity-type sections of multiTypeSuggestions (keyed by real file
      // id), NOT on uploadedFiles[].new_properties. Without this loop, a
      // multi-type AI-only select card finds no column/file id above and the
      // fetch short-circuits — "Generate options from CSV" would silently
      // no-op for it (see #11400).
      for (const [fid, mt] of Object.entries(multiTypeSuggestionsRef.current)) {
        let found: NewPropertySuggestion | undefined;
        for (const et of mt.entity_types) {
          found = et.new_properties.find(matches);
          if (found) {
            break;
          }
        }
        if (found) {
          if (!column) {
            ({ column } = found);
          }
          const numId = Number(fid);
          if (!Number.isNaN(numId) && !numericFileIds.includes(numId)) {
            numericFileIds.push(numId);
          }
        }
      }

      if (!column || numericFileIds.length === 0) {
        return;
      }

      const seqKey = optionsSeqKey(fileIdStr, propertyName, entityTypeName);
      const seq = (optionsRequestSeqRef.current[seqKey] ?? 0) + 1;
      optionsRequestSeqRef.current[seqKey] = seq;

      suggestSelectOptions(orgId, numericFileIds, column)
        .then(result => {
          setUserNewProperties(prev => {
            // A user edit (or a newer request) after this fetch was issued
            // bumps the counter; if it no longer matches, this response is
            // stale and must not overwrite the current options.
            if (optionsRequestSeqRef.current[seqKey] !== seq) {
              return prev;
            }
            // Scope the write to the file whose card was clicked. The same
            // (property, entity type) can exist across multiple files, each
            // rendered as its own independently editable card (#11400); writing
            // to the first match anywhere would land the generated options on
            // the wrong file's entry. Match on entity type too so a response
            // for one entity type can't be written onto a same-named property
            // of a different type in a multi-type file.
            const scoped = prev[fileIdStr];
            if (scoped) {
              const idx = scoped.findIndex(matches);
              if (idx !== -1) {
                // Already materialized in userNewProperties for this file.
                // Apply only if it is still a select; if the user changed the
                // type out of select while the fetch was in flight, drop the
                // stale response rather than write options onto a non-select.
                if (scoped[idx].field_type_name === 'select') {
                  return {
                    ...prev,
                    [fileIdStr]: scoped.map((np, i) =>
                      i === idx ? { ...np, options: result.options } : np
                    ),
                  };
                }
                return prev;
              }
            }
            // Not yet in userNewProperties for this file: it's still an AI
            // suggestion the user never re-typed (a select the AI proposed).
            // Copy that file's AI select suggestion in with the generated
            // options rather than dropping the response.
            const uf = uploadedFilesRef.current.find(
              f => String(f.file_id) === fileIdStr
            );
            const aiProp = uf?.new_properties?.find(matches);
            if (aiProp && aiProp.field_type_name === 'select') {
              const existing = prev[fileIdStr] ?? [];
              return {
                ...prev,
                [fileIdStr]: [
                  ...existing,
                  { ...aiProp, options: result.options },
                ],
              };
            }
            // Not standard-AI either — for a multi-type file the AI select
            // suggestion lives on the per-entity-type sections keyed by file
            // id. Materialize that file's entry with the generated options so
            // the response lands rather than being dropped (see #11400).
            const mt = multiTypeSuggestionsRef.current[fileIdStr];
            if (mt) {
              for (const et of mt.entity_types) {
                const mtAiProp = et.new_properties.find(matches);
                if (mtAiProp && mtAiProp.field_type_name === 'select') {
                  const existing = prev[fileIdStr] ?? [];
                  return {
                    ...prev,
                    [fileIdStr]: [
                      ...existing,
                      { ...mtAiProp, options: result.options },
                    ],
                  };
                }
              }
            }
            return prev;
          });
        })
        .catch(err => {
          logger.error(
            'Failed to generate suggested select options',
            err instanceof Error ? err : new Error(String(err)),
            { propertyName, entityTypeName }
          );
        });
    },
    [orgId]
  );

  // Update the options of a new property directly (e.g. from the options editor UI).
  const handleUserNewPropertyOptionsChange = useCallback(
    (
      fileId: string | number,
      propertyName: string,
      entityTypeName: string,
      options: SelectOptionSuggestion[]
    ) => {
      const fileIdStr = String(fileId);
      const matches = (np: NewPropertySuggestion): boolean =>
        np.property_name === propertyName &&
        np.entity_type_name === entityTypeName;

      // A field-type change into 'select' copies the AI-suggested property into
      // userNewProperties and issues a suggestSelectOptions fetch, but the state
      // write is async: this callback can fire (for a type-only change) before
      // the copy has rendered, so the property is not yet in the ref above.
      // findExistingNewProperty's fallback to the AI suggestion (standard or
      // multi-type) covers this so the no-op short-circuit still fires —
      // otherwise the generation bump below cancels the in-flight fetch and the
      // select is left without its suggested options.
      const existingOptionsProp = findExistingNewProperty(fileIdStr, matches);
      if (
        existingOptionsProp &&
        optionsKey(existingOptionsProp.options) === optionsKey(options)
      ) {
        return;
      }

      // Invalidate any in-flight suggest fetch for this entry — this is a
      // genuine edit, so a late response for the prior request must not
      // overwrite it.
      const seqKey = optionsSeqKey(fileIdStr, propertyName, entityTypeName);
      optionsRequestSeqRef.current[seqKey] =
        (optionsRequestSeqRef.current[seqKey] ?? 0) + 1;

      setUserNewProperties(prev =>
        applyNewPropertyEdit(prev, fileIdStr, matches, np => ({
          ...np,
          options,
        }))
      );
    },
    [findExistingNewProperty, applyNewPropertyEdit]
  );

  // Update the label of a new property directly (e.g. from the New Properties
  // panel form). Follows the same immutable-update pattern as
  // handleUserNewPropertyOptionsChange for locating and updating a property
  // (property_name is derived once at creation and never re-derived from a
  // label edit), including its fallback to multi-type AI suggestions (via
  // multiTypeSuggestionsRef): any panel edit — label or options — must be able
  // to materialize a property that only exists in a multi-type file's
  // per-entity-type suggestions section (see #11333). Every lookup is keyed on
  // (property_name, entity_type_name) so a label edit lands on the exact
  // property the panel is editing, not the first same-named property in a
  // different entity type of a multi-type file.
  const handleUserNewPropertyLabelChange = useCallback(
    (
      fileId: string | number,
      propertyName: string,
      entityTypeName: string,
      newLabel: string
    ) => {
      const fileIdStr = String(fileId);
      const matches = (np: NewPropertySuggestion): boolean =>
        np.property_name === propertyName &&
        np.entity_type_name === entityTypeName;

      const existingLabelProp = findExistingNewProperty(fileIdStr, matches);
      if (existingLabelProp && newLabel === existingLabelProp.label) {
        return;
      }

      setUserNewProperties(prev =>
        applyNewPropertyEdit(prev, fileIdStr, matches, np => ({
          ...np,
          label: newLabel,
        }))
      );
    },
    [findExistingNewProperty, applyNewPropertyEdit]
  );

  return {
    userNewProperties,
    setUserNewProperties,
    handleCreateProperty,
    handleUserNewPropertyFieldTypeChange,
    handleUserNewPropertyOptionsChange,
    handleUserNewPropertyLabelChange,
    handleGenerateSelectOptions,
  };
}
